import React from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Box, Button, Heading, Spinner, Text } from '@indeed/ifl-components';
import { TrashCan } from '@indeed/ifl-icons';
import { useGetJobInfoHeaderDataQuery } from './__generated__/JobInfoHeader.generated';
import { JobInfoHeaderFieldsFragment } from './__generated__/JobInfoHeaderFragments.generated';
import GettextFactory from '../../../util/GettextFactory';
import { useDeleteAutoApplyApplicationDraft } from '../hooks/useDeleteAutoApplyApplicationDraft';
import { useEventLogger } from '../../../lib/eventLogger/EventLoggerProvider';
import { AvroOptional } from '@indeed/signals-schema/com/indeed/signals/schema/indeedapply/AutoApplyReviewButtonClickEvent';

export type JobInfoHeaderProps = {
    /** Callback invoked when user clicks the remove/delete button. Receives the deletion Promise. */
    onDelete?: (deletionPromise: Promise<unknown>) => void;
    /**
     * Pre-fetched draft data from a parent query. When provided the component
     * skips its own GraphQL query and uses this data directly.
     */
    fragment?: JobInfoHeaderFieldsFragment;
    /** Whether deletion is currently in progress (for button disabled state) */
    isDeleting?: boolean;
};

/**
 * JobInfoHeader Component
 *
 * Standalone component that displays job information (title, company, location)
 * with a delete/remove action for the current draft.
 *
 * Uses `useParams` to read the `draftId` from the URL and fetches job details
 * via the `findReviewBeforeSubmitDraft` GraphQL query using the draft ID.
 *
 * GraphQL Query: GetJobInfoHeaderData
 * - Uses `findReviewBeforeSubmitDraft` with `applicationDraftId` input
 * - Fetches: jobTitle, jobLocation, employerName from draft
 * - Query structure matches ReviewPage.graphql to leverage Apollo cache
 */
const JobInfoHeader: React.FC<JobInfoHeaderProps> = ({
    onDelete,
    fragment,
    isDeleting = false,
}) => {
    const gt = GettextFactory();
    const { draftId } = useParams<{ draftId: string }>();
    const routerLocation = useLocation();
    const eventLogger = useEventLogger();

    // Only run the query when the parent hasn't provided pre-fetched data
    const { data, loading, error } = useGetJobInfoHeaderDataQuery({
        variables: {
            input: {
                applicationDraftId: draftId || '',
                skipSourceValidation: true,
            },
        },
        skip: !!fragment || !draftId,
        errorPolicy: 'all',
        fetchPolicy: 'cache-first',
    });

    const deleteAutoApplyApplicationDraft = useDeleteAutoApplyApplicationDraft();

    // Use parent-provided fragment data if available, otherwise fall back to query result
    const draft = fragment ?? data?.findReviewBeforeSubmitDraft?.draft;

    const jobTitle = draft?.jobTitle;
    const jobKey = draft?.applyableJob?.clientJobRef;
    const company = draft?.employerName;
    const jobLocation = draft?.jobLocation;

    // Show loading state only when fetching via query (not when data comes from fragment)
    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 2,
                }}
            >
                <Spinner title={gt.gettext('Loading job info...')} />
            </Box>
        );
    }

    // Don't render if no job data available (error or missing draft)
    if (error || !jobTitle || !draftId) {
        return null;
    }

    // Build company + location display string
    const companyLocation = [company, jobLocation].filter(Boolean).join(' · ');

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                padding: 2,
            }}
        >
            <Box sx={{ flex: 1 }}>
                <Heading
                    level={3}
                    sx={{
                        display: 'block',
                        marginBlockEnd: '4px',
                    }}
                >
                    {jobTitle}
                </Heading>
                {companyLocation && <Text level={2}>{companyLocation}</Text>}
            </Box>
            <Button
                variant="tertiary"
                size="sm"
                onClick={() => {
                    eventLogger.logReviewButtonClick?.({
                        currentPage: routerLocation.pathname,
                        button: 'job-info-delete-draft',
                        draftId: AvroOptional.string(draftId),
                    });
                    const deletionPromise = deleteAutoApplyApplicationDraft(draftId, jobKey);
                    // The delete mutation rejects on failure. `onDelete` observes that (to decide
                    // whether to advance), but attach a safety-net catch on this same promise so a
                    // rejection never surfaces as an unhandled rejection if the consumer doesn't
                    // attach its own handler — both handlers see the outcome independently.
                    deletionPromise.catch(() => {});
                    onDelete?.(deletionPromise);
                }}
                aria-label={gt.gettext('Remove job')}
                disabled={isDeleting}
                loading={isDeleting}
                sx={{
                    flexShrink: 0,
                    // Neutral (black) trash icon per UX, not the tertiary button's default brand blue.
                    color: 'foreground.strong',
                }}
            >
                <TrashCan size="md" variant="stroke" aria-hidden />
            </Button>
        </Box>
    );
};

export default JobInfoHeader;
